import React, { useState, useEffect, useCallback } from 'react';
import { 
  User,
  Mail,
  FolderOpen,
  RefreshCw,
  FileText,
  Download,
  Eye,
  Clock,
  Image as ImageIcon,
  File,
  Edit,
  X,
  Save,
  AlertCircle,
  Trash2,
  Upload,
  Undo
} from 'lucide-react';
import api from '../services/api';

interface SubmittedProject {
  id: number;
  title: string;
  description: string;
  message: string;
  files: any[];
  files_count: number;
  status: string;
  status_display: string;
  status_color: string;
  user_name?: string;
  user_email?: string;
  admin_response: string;
  created_at: string;
}

interface SubmittedProjectsTabProps {
  isAdmin?: boolean;
}

const SubmittedProjectsTab: React.FC<SubmittedProjectsTabProps> = ({ isAdmin = false }) => {
  const [projects, setProjects] = useState<SubmittedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editingProject, setEditingProject] = useState<SubmittedProject | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const formatDate = (dateString: string | null | undefined) => {
    try {
      if (!dateString || dateString === '') {
        return 'Date non disponible';
      }
      
      // Nettoyer la chaîne de date
      const cleanedDate = String(dateString).trim();
      const date = new Date(cleanedDate);
      
      if (isNaN(date.getTime())) {
        console.error('❌ Date invalide:', cleanedDate);
        return 'Date non disponible';
      }
      
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('❌ Erreur formatage date:', error);
      return 'Date non disponible';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'reviewing': 'bg-blue-100 text-blue-800 border-blue-200',
      'accepted': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'in_progress': 'bg-purple-100 text-purple-800 border-purple-200',
      'completed': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const isImageFile = (fileName: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  // Fonction pour obtenir l'URL d'affichage selon le type de fichier
  const getFileUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      // PDF stocké dans PostgreSQL
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/`;
    } else {
      // Fichier Cloudinary (image, word, etc) ou ancien format
      return file.url || file.file_url || file.file || '';
    }
  };

  // Fonction pour obtenir l'URL de téléchargement
  const getDownloadUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/download/`;
    } else {
      return file.url || file.file_url || file.file || '';
    }
  };

  const handleViewFile = (file: any, fileName: string) => {
    const fileUrl = getFileUrl(file);
    console.log('📂 Opening file:', fileName, 'Type:', file.type, 'URL:', fileUrl);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const handleDownloadFile = (fileUrl: string, fileName: string) => {
    console.log('⬇️ Downloading file:', fileName, 'URL:', fileUrl);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'fichier';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = isAdmin 
        ? '/api/submitted-projects/all/'
        : '/api/submitted-projects/user/';
      
      const response = await api.get(endpoint);
      const data = response.data;
      if (data.success) {
        console.log('📦 Données brutes reçues:', JSON.stringify(data.projects, null, 2));
        setProjects(data.projects);
        console.log('✅ Projets chargés:', data.projects.length, 'projets');
        data.projects.forEach((project: SubmittedProject, index: number) => {
          console.log(`📅 Date du projet "${project.title}":`, project.created_at, 'Type:', typeof project.created_at);
          console.log(`📅 Test formatage:`, formatDate(project.created_at));
          if (project.files && project.files.length > 0) {
            console.log(`📁 Projet "${project.title}" contient ${project.files.length} fichier(s):`);
            project.files.forEach((file: any, fileIndex: number) => {
              console.log(`  ${fileIndex + 1}. ${file.file_name || 'Sans nom'} - URL: ${file.file_url || file.file || 'Pas d\'URL'}`);
            });
          }
        });
      } else {
        setError('Erreur lors du chargement des projets soumis');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des projets soumis:', err);
      setError('Erreur lors du chargement des projets soumis');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleOpenEditModal = (project: SubmittedProject) => {
    setEditingProject(project);
    setEditForm({
      title: project.title,
      description: project.description,
      message: project.message || ''
    });
    setFilesToDelete([]);
    setNewFiles([]);
  };

  const handleCloseEditModal = () => {
    setEditingProject(null);
    setEditForm({ title: '', description: '', message: '' });
    setFilesToDelete([]);
    setNewFiles([]);
  };

  const handleDeleteFile = (savedPath: string) => {
    setFilesToDelete(prev => [...prev, savedPath]);
  };

  const handleUndoDeleteFile = (savedPath: string) => {
    setFilesToDelete(prev => prev.filter(path => path !== savedPath));
  };

  const handleAddNewFiles = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    setNewFiles(prev => [...prev, ...fileArray]);
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = async () => {
    if (!editingProject) return;

    // Validation
    if (!editForm.title.trim()) {
      alert('Le titre est obligatoire');
      return;
    }
    if (!editForm.description.trim()) {
      alert('La description est obligatoire');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      // Utiliser FormData pour envoyer les fichiers
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      formData.append('message', editForm.message);
      
      // Ajouter les fichiers à supprimer
      if (filesToDelete.length > 0) {
        formData.append('files_to_delete', JSON.stringify(filesToDelete));
      }
      
      // Ajouter les nouveaux fichiers
      newFiles.forEach(file => {
        formData.append('new_files', file);
      });
      
      const response = await api.put(`/api/submitted-projects/${editingProject.id}/edit/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      
      if (data.success) {
        // Mettre à jour le projet dans la liste
        setProjects(prev => prev.map(p => 
          p.id === editingProject.id ? data.project : p
        ));
        handleCloseEditModal();
        alert('Projet modifié avec succès');
      } else {
        alert(data.message || data.error || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du projet');
    } finally {
      setSaving(false);
    }
  };

  const refreshProjects = async () => {
    try {
      setRefreshing(true);
      await loadProjects();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des projets soumis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FolderOpen className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshProjects}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête avec bouton actualiser */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isAdmin ? 'Projets Personnels Soumis' : 'Mes Projets Personnels'}
          </h2>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Projets soumis par les utilisateurs via le formulaire de soumission'
              : 'Projets que vous avez soumis via le formulaire de soumission'
            }
          </p>
        </div>
        <button
          onClick={refreshProjects}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FolderOpen className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 text-sm font-bold">⏳</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">👁️</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En examen</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'reviewing').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm font-bold">✅</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Acceptés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'accepted').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des projets en grille de cartes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
            {/* En-tête de la carte */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-bold text-white line-clamp-2">{project.title}</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeColor(project.status)}`}>
                  {project.status_display}
                </span>
              </div>
            </div>

            {/* Contenu de la carte */}
            <div className="p-5">
              <p className="text-gray-700 text-sm mb-4 line-clamp-3">{project.description}</p>
                  
              {/* Informations utilisateur (pour admin) */}
              {isAdmin && (
                <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-gray-900">{project.user_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span className="text-gray-600">{project.user_email}</span>
                    </div>
                  </div>
                </div>
              )}
                  
              {/* Message additionnel */}
              {project.message && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <p className="text-xs font-semibold text-blue-900 mb-1 uppercase">
                    💬 {isAdmin ? 'Message du client' : 'Votre message'}
                  </p>
                  <p className="text-sm text-blue-800 line-clamp-2">{project.message}</p>
                </div>
              )}

              {/* Fichiers joints - Miniatures cliquables */}
              {project.files && project.files.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase flex items-center gap-1">
                      <FileText className="w-4 h-4 text-purple-600" />
                      Fichiers joints ({project.files.length})
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {project.files.map((file: any, index: number) => {
                      const fileName = file.original_name || file.file_name || file.name || `Fichier ${index + 1}`;
                      const fileUrl = getFileUrl(file);
                      const isImage = isImageFile(fileName);
                      const isPDF = file.type === 'pdf' || fileName.toLowerCase().endsWith('.pdf');
                      
                      return (
                        <div 
                          key={index}
                          onClick={() => handleViewFile(file, fileName)}
                          className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-400 cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden"
                        >
                          {/* Miniature */}
                          <div className="aspect-square flex items-center justify-center p-3">
                            {isImage ? (
                              <div className="relative w-full h-full">
                                <img 
                                  src={fileUrl} 
                                  alt={fileName}
                                  className="w-full h-full object-cover rounded"
                                  onError={(e) => {
                                    // Si l'image ne charge pas, afficher une icône à la place
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                                <div className="hidden w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-12 h-12 text-purple-400" />
                                </div>
                              </div>
                            ) : (
                              <File className="w-12 h-12 text-purple-400" />
                            )}
                          </div>
                          
                          {/* Overlay au hover */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                          
                          {/* Nom du fichier */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                            <p className="text-xs text-white font-medium truncate">
                              {fileName}
                            </p>
                          </div>
                          
                          {/* Bouton téléchargement */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(fileUrl, fileName);
                            }}
                            className="absolute top-1 right-1 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            title="Télécharger"
                          >
                            <Download className="w-3 h-3 text-purple-600" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Réponse de l'équipe */}
              {project.admin_response && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                  <p className="text-xs font-semibold text-green-900 mb-1 uppercase">✅ Réponse de l'équipe</p>
                  <p className="text-sm text-green-800 line-clamp-2">{project.admin_response}</p>
                </div>
              )}

              {/* Footer de la carte */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(project.created_at)}</span>
                  </div>
                  
                  {/* Bouton Modifier - visible uniquement si statut = pending et non-admin */}
                  {!isAdmin && project.status === 'pending' && (
                    <button
                      onClick={() => handleOpenEditModal(project)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                    >
                      <Edit className="w-3 h-3" />
                      Modifier
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isAdmin ? 'Aucun projet soumis' : 'Aucun projet personnel'}
            </h3>
            <p className="text-gray-600">
              {isAdmin 
                ? 'Les projets soumis par les utilisateurs apparaîtront ici.'
                : (
                  <>
                    Vous n'avez pas encore soumis de projet personnel. 
                    <br />
                    Utilisez le bouton "Soumettre un projet" sur la page d'accueil pour commencer.
                  </>
                )
              }
            </p>
          </div>
        )}
      </div>

      {/* Modal de modification */}
      {editingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* En-tête du modal */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Edit className="w-6 h-6" />
                  Modifier le projet
                </h3>
                <button
                  onClick={handleCloseEditModal}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <p className="text-blue-100 text-sm mt-2">
                Modifiez les informations de votre projet
              </p>
            </div>

            {/* Contenu du modal */}
            <div className="p-6 space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Titre du projet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Ex: Développement site web e-commerce"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors resize-none"
                  placeholder="Décrivez votre projet en détail..."
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message additionnel
                </label>
                <textarea
                  value={editForm.message}
                  onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors resize-none"
                  placeholder="Informations supplémentaires (optionnel)..."
                />
              </div>

              {/* Gestion des fichiers existants */}
              {editingProject && editingProject.files && editingProject.files.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fichiers actuels
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {editingProject.files.map((file: any, index: number) => {
                      const fileName = file.original_name || file.file_name || `Fichier ${index + 1}`;
                      const savedPath = file.saved_path || file.pdf_id || '';
                      const isMarkedForDeletion = filesToDelete.includes(savedPath);
                      const isImage = isImageFile(fileName);
                      
                      return (
                        <div
                          key={index}
                          className={`relative bg-gray-50 rounded-lg border-2 p-3 transition-all ${
                            isMarkedForDeletion 
                              ? 'border-red-300 bg-red-50 opacity-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isImage ? <ImageIcon className="w-4 h-4 text-gray-400" /> : <File className="w-4 h-4 text-gray-400" />}
                            <span className={`text-xs flex-1 truncate ${isMarkedForDeletion ? 'text-red-600 line-through' : 'text-gray-700'}`}>
                              {fileName}
                            </span>
                          </div>
                          
                          {isMarkedForDeletion ? (
                            <button
                              onClick={() => handleUndoDeleteFile(savedPath)}
                              className="absolute top-2 right-2 p-1 bg-green-100 hover:bg-green-200 rounded transition-colors"
                              title="Annuler la suppression"
                            >
                              <Undo className="w-3 h-3 text-green-600" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteFile(savedPath)}
                              className="absolute top-2 right-2 p-1 bg-red-100 hover:bg-red-200 rounded transition-colors"
                              title="Supprimer ce fichier"
                            >
                              <Trash2 className="w-3 h-3 text-red-600" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filesToDelete.length > 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      {filesToDelete.length} fichier(s) sera supprimé lors de la sauvegarde
                    </p>
                  )}
                </div>
              )}

              {/* Ajout de nouveaux fichiers */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ajouter des fichiers
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleAddNewFiles(e.target.files)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-700">
                      Cliquez pour ajouter des fichiers
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      Images, PDF, documents...
                    </span>
                  </label>
                </div>

                {/* Liste des nouveaux fichiers à uploader */}
                {newFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-semibold text-green-700">
                      Nouveaux fichiers à ajouter ({newFiles.length}) :
                    </p>
                    {newFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-green-900 truncate">{file.name}</span>
                          <span className="text-xs text-green-600">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          onClick={() => handleRemoveNewFile(index)}
                          className="p-1 hover:bg-green-200 rounded transition-colors"
                          title="Retirer"
                        >
                          <X className="w-3 h-3 text-green-700" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Note importante */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Note importante
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Vous ne pouvez modifier ce projet que tant qu'il est en statut "En attente". 
                      Une fois examiné par l'équipe, les modifications ne seront plus possibles.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer du modal */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex items-center justify-end gap-3">
              <button
                onClick={handleCloseEditModal}
                disabled={saving}
                className="px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmittedProjectsTab;
